import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  lpSessions,
  type LPSession,
  type LPSessionCreate,
} from "@/api/lpSessions";
import { ApiError } from "@/api/http";
import { Modal } from "@/components/Modal";
import { useToast } from "@/components/Toast";
import {
  Badge,
  Button,
  Card,
  CardBody,
  EmptyState,
  FormError,
  Input,
  Label,
  Select,
} from "@/components/ui";

/**
 * Admin page for `lp_sessions` rows — create / patch / delete LP
 * connections, peek at health + recent FIX/REST messages, toggle
 * enabled. Credentials are write-only (the API exposes only `creds_set`).
 */
export function AdminLpSessionsPage() {
  const list = useQuery({ queryKey: ["lp-sessions"], queryFn: () => lpSessions.list() });
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState<LPSession | null>(null);
  const [inspecting, setInspecting] = useState<LPSession | null>(null);
  const toast = useToast();
  const qc = useQueryClient();

  const testLogon = useMutation({
    mutationFn: (id: string) => lpSessions.testLogon(id),
    onSuccess: (r) => {
      toast.push(
        r.connected ? `Logged on (${r.status})` : `Logon failed: ${r.last_error ?? r.status}`,
        r.connected ? "success" : "error",
      );
      qc.invalidateQueries({ queryKey: ["lp-sessions"] });
    },
    onError: (e) =>
      toast.push(e instanceof ApiError ? e.message.replace(/^\d+:\s*/, "") : String(e), "error"),
  });

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        <h1 className="text-base font-semibold text-term-fg">LP sessions</h1>
        <Button onClick={() => { setEditing(null); setOpen(true); }}>New session</Button>
      </div>

      <Card>
        <CardBody className="p-0">
          {list.isLoading ? (
            <div className="p-3 text-sm text-term-mute">Loading…</div>
          ) : list.data?.length === 0 ? (
            <EmptyState
              title="No LP sessions configured"
              description="Each session models one upstream FIX/REST connection. The supervisor opens enabled sessions on app boot."
              action={<Button onClick={() => setOpen(true)}>Add first session</Button>}
            />
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-term-elev text-term-mute">
                <tr>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Name</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Asset</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Protocol</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Status</th>
                  <th className="px-3 py-1.5 text-right text-2xs uppercase tracking-wider">Priority</th>
                  <th className="px-3 py-1.5 text-left text-2xs uppercase tracking-wider">Enabled</th>
                  <th className="px-3 py-1.5"></th>
                </tr>
              </thead>
              <tbody>
                {list.data?.map((s) => (
                  <tr key={s.id} className="border-t border-term-border hover:bg-term-elev">
                    <td className="px-3 py-1.5 font-medium text-term-fg">{s.name}</td>
                    <td className="px-3 py-1.5 font-mono text-term-dim">{s.asset_class}</td>
                    <td className="px-3 py-1.5 font-mono text-term-dim">{s.protocol}</td>
                    <td className="px-3 py-1.5">
                      <Badge tone={statusTone(s.status)}>{s.status}</Badge>
                    </td>
                    <td className="px-3 py-1.5 text-right tnum text-term-dim">{s.priority}</td>
                    <td className="px-3 py-1.5">
                      <Badge tone={s.enabled ? "good" : "neutral"}>{s.enabled ? "ON" : "OFF"}</Badge>
                    </td>
                    <td className="px-3 py-1.5 text-right">
                      <div className="flex justify-end gap-2">
                        <Button
                          variant="ghost"
                          onClick={() => testLogon.mutate(s.id)}
                          disabled={testLogon.isPending}
                        >
                          Test logon
                        </Button>
                        <Button variant="ghost" onClick={() => setInspecting(s)}>Health</Button>
                        <Button variant="ghost" onClick={() => { setEditing(s); setOpen(true); }}>Edit</Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardBody>
      </Card>

      {open && (
        <SessionFormModal
          session={editing}
          onClose={() => { setOpen(false); setEditing(null); }}
        />
      )}
      {inspecting && (
        <HealthModal session={inspecting} onClose={() => setInspecting(null)} />
      )}
    </div>
  );
}

function statusTone(s: string): "good" | "warn" | "bad" | "neutral" {
  if (s === "active") return "good";
  if (s === "disconnected" || s === "logging_on") return "warn";
  if (s === "error") return "bad";
  return "neutral";
}

function SessionFormModal({
  session,
  onClose,
}: {
  session: LPSession | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const toast = useToast();
  const [form, setForm] = useState<LPSessionCreate>(() => ({
    name: session?.name ?? "",
    asset_class: session?.asset_class ?? "FX",
    protocol: session?.protocol ?? "FIX44",
    host: session?.host ?? "",
    port: session?.port ?? undefined,
    sender_comp_id: session?.sender_comp_id ?? "",
    target_comp_id: session?.target_comp_id ?? "",
    heartbeat_int: session?.heartbeat_int ?? 30,
    enabled: session?.enabled ?? false,
    priority: session?.priority ?? 100,
    instruments: session?.instruments ?? [],
  }));
  const [instrumentText, setInstrumentText] = useState(
    (session?.instruments ?? []).join(", ")
  );
  const [err, setErr] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: async () => {
      const body: LPSessionCreate = {
        ...form,
        instruments: instrumentText.split(",").map((s) => s.trim()).filter(Boolean),
      };
      if (session) return lpSessions.patch(session.id, body);
      return lpSessions.create(body);
    },
    onSuccess: () => {
      toast.push(session ? "LP session updated" : "LP session created", "success");
      qc.invalidateQueries({ queryKey: ["lp-sessions"] });
      onClose();
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message : String(e)),
  });

  const remove = useMutation({
    mutationFn: () => lpSessions.remove(session!.id),
    onSuccess: () => {
      toast.push("LP session deleted", "warn");
      qc.invalidateQueries({ queryKey: ["lp-sessions"] });
      onClose();
    },
    onError: (e) => setErr(e instanceof ApiError ? e.message : String(e)),
  });

  return (
    <Modal open onClose={onClose} title={session ? `Edit ${session.name}` : "New LP session"}>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <div>
            <Label>Name</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
          </div>
          <div>
            <Label>Asset class</Label>
            <Select
              value={form.asset_class}
              onChange={(e) => setForm({ ...form, asset_class: e.target.value })}
            >
              <option value="FX">FX</option>
              <option value="EQUITY">EQUITY</option>
              <option value="CRYPTO">CRYPTO</option>
              <option value="MULTI">MULTI</option>
            </Select>
          </div>
          <div>
            <Label>Protocol</Label>
            <Select
              value={form.protocol}
              onChange={(e) => setForm({ ...form, protocol: e.target.value })}
            >
              <option value="FIX44">FIX 4.4</option>
              <option value="REST">REST</option>
              <option value="WS">WS</option>
              <option value="MOCK">MOCK</option>
            </Select>
          </div>
          <div>
            <Label>Priority</Label>
            <Input
              type="number"
              value={form.priority ?? 100}
              onChange={(e) => setForm({ ...form, priority: Number(e.target.value) })}
            />
          </div>
          <div>
            <Label>Host</Label>
            <Input
              value={form.host ?? ""}
              onChange={(e) => setForm({ ...form, host: e.target.value })}
            />
          </div>
          <div>
            <Label>Port</Label>
            <Input
              type="number"
              value={form.port ?? ""}
              onChange={(e) => setForm({ ...form, port: e.target.value === "" ? undefined : Number(e.target.value) })}
            />
          </div>
          <div>
            <Label>Sender comp id</Label>
            <Input
              value={form.sender_comp_id ?? ""}
              onChange={(e) => setForm({ ...form, sender_comp_id: e.target.value })}
            />
          </div>
          <div>
            <Label>Target comp id</Label>
            <Input
              value={form.target_comp_id ?? ""}
              onChange={(e) => setForm({ ...form, target_comp_id: e.target.value })}
            />
          </div>
          <div>
            <Label>Heartbeat (s)</Label>
            <Input
              type="number"
              value={form.heartbeat_int ?? 30}
              onChange={(e) => setForm({ ...form, heartbeat_int: Number(e.target.value) })}
            />
          </div>
          <div className="flex items-end gap-2">
            <input
              id="enabled"
              type="checkbox"
              checked={!!form.enabled}
              onChange={(e) => setForm({ ...form, enabled: e.target.checked })}
            />
            <label htmlFor="enabled" className="text-xs uppercase tracking-wider text-term-dim">
              Enabled
            </label>
          </div>
          <div className="col-span-2">
            <Label>Instruments (comma separated)</Label>
            <Input
              placeholder="EUR/USD, GBP/USD, BTC/USDT"
              value={instrumentText}
              onChange={(e) => setInstrumentText(e.target.value)}
            />
          </div>
          <div className="col-span-2 grid grid-cols-2 gap-3">
            <div>
              <Label>Username (write-only)</Label>
              <Input
                placeholder={session?.creds_set ? "•••• (set)" : "blank"}
                value={form.username ?? ""}
                onChange={(e) => setForm({ ...form, username: e.target.value })}
              />
            </div>
            <div>
              <Label>Password (write-only)</Label>
              <Input
                type="password"
                placeholder={session?.creds_set ? "•••• (set)" : "blank"}
                value={form.password ?? ""}
                onChange={(e) => setForm({ ...form, password: e.target.value })}
              />
            </div>
          </div>
        </div>

        <FormError>{err}</FormError>

        <div className="flex items-center justify-between pt-2">
          <div>
            {session && (
              <Button
                variant="danger"
                onClick={() => {
                  if (confirm(`Delete LP session "${session.name}"?`)) remove.mutate();
                }}
                disabled={remove.isPending}
              >
                Delete
              </Button>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="ghost" onClick={onClose}>Cancel</Button>
            <Button onClick={() => save.mutate()} disabled={save.isPending || !form.name}>
              {session ? "Save" : "Create"}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
}

function HealthModal({ session, onClose }: { session: LPSession; onClose: () => void }) {
  const health = useQuery({
    queryKey: ["lp-sessions", session.id, "health"],
    queryFn: () => lpSessions.health(session.id),
    refetchInterval: 3000,
  });
  const messages = useQuery({
    queryKey: ["lp-sessions", session.id, "messages"],
    queryFn: () => lpSessions.messages(session.id, 50),
    refetchInterval: 3000,
  });

  return (
    <Modal open onClose={onClose} title={`Health · ${session.name}`}>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-2 rounded-sm border border-term-border bg-term-elev p-2 text-xs">
          <Field label="Row status" value={health.data?.status ?? "—"} />
          <Field
            label="Live connected"
            value={
              health.data?.live ? (health.data.live.connected ? "yes" : "no") : "—"
            }
          />
          <Field label="Last in seq" value={String(health.data?.last_in_seq ?? "—")} />
          <Field label="Last out seq" value={String(health.data?.last_out_seq ?? "—")} />
          <Field
            label="Reject %"
            value={
              health.data?.live?.reject_rate_pct == null
                ? "—"
                : Number(health.data.live.reject_rate_pct).toFixed(2)
            }
          />
          <Field label="Last error" value={health.data?.last_error ?? "—"} />
        </div>

        <div>
          <div className="mb-1 text-2xs font-medium uppercase tracking-wider text-term-mute">
            Recent messages
          </div>
          <div className="max-h-72 overflow-y-auto rounded-sm border border-term-border bg-term-bg">
            {messages.isLoading ? (
              <div className="p-2 text-xs text-term-mute">Loading…</div>
            ) : messages.data?.length === 0 ? (
              <div className="p-2 text-xs text-term-mute">No messages logged.</div>
            ) : (
              <table className="w-full text-2xs">
                <thead className="bg-term-elev text-term-mute">
                  <tr>
                    <th className="px-2 py-1 text-left">Time</th>
                    <th className="px-2 py-1 text-left">Dir</th>
                    <th className="px-2 py-1 text-left">Type</th>
                    <th className="px-2 py-1 text-right">Seq</th>
                    <th className="px-2 py-1 text-left">Raw</th>
                  </tr>
                </thead>
                <tbody>
                  {messages.data?.map((m) => (
                    <tr key={m.id} className="border-t border-term-border">
                      <td className="px-2 py-0.5 tnum text-term-dim">
                        {new Date(m.ts).toLocaleTimeString()}
                      </td>
                      <td className="px-2 py-0.5">
                        <Badge tone={m.direction === "OUT" ? "good" : "neutral"}>
                          {m.direction}
                        </Badge>
                      </td>
                      <td className="px-2 py-0.5 font-mono text-term-fg">{m.msg_type}</td>
                      <td className="px-2 py-0.5 text-right tnum text-term-dim">
                        {m.seq_num ?? "—"}
                      </td>
                      <td className="px-2 py-0.5 truncate font-mono text-term-dim" style={{ maxWidth: "24rem" }}>
                        {m.raw}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between">
      <span className="uppercase tracking-wider text-term-mute">{label}</span>
      <span className="font-mono text-term-fg">{value}</span>
    </div>
  );
}
